import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Book, Check, ChevronRight } from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import LogoScroller from '@/components/LogoScroller';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';

const BookStand = () => {
  return (
    <div className="min-h-screen bg-ai-dark">
      <Helmet>
        <title>Book Exhibit Space - AI World Congress 2026 - November 25-26, London UK - AI Conference</title>
        <meta name="description" content="Showcase your AI solutions at AI World Congress 2026. Connect with industry leaders, generate high-quality leads, and network with decision-makers in London's premier AI conference." />
        <link rel="canonical" href="https://aiconference.london/exhibition" />
        <meta property="og:title" content="Book Exhibit Space - AI World Congress 2026 - November 25-26, London UK - AI Conference" />
        <meta property="og:description" content="Showcase your AI solutions at AI World Congress 2026. Connect with industry leaders in London." />
        <meta property="og:url" content="https://aiconference.london/exhibition" />
        <meta property="og:image" content="https://aiconference.london/lovable-uploads/og-banner-2026.jpg" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Book Exhibit Space - AI World Congress 2026 - November 25-26, London UK - AI Conference" />
        <meta name="twitter:description" content="Exhibit at AI World Congress 2026 in London." />
        <meta name="twitter:image" content="https://aiconference.london/lovable-uploads/og-banner-2026.jpg" />
      </Helmet>
      <Header />
      <PageHeader 
        title=""
        backgroundImage="/lovable-uploads/530fddbf-4fea-4198-860f-8165d2f8b445.png"
      />
      
      <div className="relative z-10 -mt-16">
        <div className="relative py-16 md:py-24 overflow-hidden bg-gradient-to-b from-purple-900/80 to-black">
          <div className="absolute inset-0 w-full h-full overflow-hidden z-0">
            <div className="absolute w-full h-[40%] bottom-0 bg-gradient-to-t from-black to-transparent"></div>
            <div className="absolute w-full h-[500px] bottom-0 left-0 right-0">
              <svg className="absolute bottom-0 w-full" viewBox="0 0 1440 320" xmlns="http://www.w3.org/2000/svg">
                <path 
                  fill="#000000" 
                  fillOpacity="1" 
                  d="M0,128L48,138.7C96,149,192,171,288,176C384,181,480,171,576,144C672,117,768,75,864,80C960,85,1056,139,1152,160C1248,181,1344,171,1392,165.3L1440,160L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
                  className="animate-pulse"
                  style={{ animationDuration: '15s' }}
                ></path>
              </svg>
            </div>
          </div>
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="text-center max-w-4xl mx-auto">
              <h1 className="text-4xl md:text-5xl font-bold mb-8">
                <span className="text-white">AI Exhibition </span>
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">2026</span>
              </h1>
              
              <p className="text-xl text-gray-300 mb-12 max-w-3xl mx-auto animate-fade-in" style={{ animationDelay: '0.2s' }}>
                2026 Dates: November 25-26, 2026. Please choose one of three AI Exhibition packages below for AI World Congress 2026
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 pt-10">
        <div className="max-w-5xl mx-auto">
          {/* Detailed Exhibit Packages - Moved above "Why Exhibit?" section */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-white mb-8 text-center">Detailed Exhibit Packages</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {/* Package 1 */}
              <Card className="bg-gradient-to-br from-purple-900 via-purple-900/80 to-black border border-purple-500/30 overflow-hidden relative rounded-xl shadow-lg shadow-purple-900/20 hover:shadow-purple-800/30 hover:-translate-y-1 transition-all duration-300">
                <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-blue-500 to-purple-600"></div>
                <div className="p-6">
                  <div className="mb-6 text-center">
                    <h3 className="text-xl font-bold text-gray-300 uppercase tracking-wider mb-1">Package 1</h3>
                    <div className="flex justify-center items-baseline">
                      <span className="text-5xl font-bold text-white">£2,850</span>
                      <span className="text-gray-400 ml-1">+VAT</span>
                    </div>
                  </div>
                  <Separator className="bg-purple-600/40 my-6" />
                  <ul className="space-y-0 mb-8">
                    <li className="flex items-start py-2">
                      <Check className="h-5 w-5 text-green-500 mr-2 shrink-0 mt-0.5" />
                      <span className="text-gray-300">2 x Delegate Passes</span>
                    </li>
                    <Separator className="bg-white/25 my-1" />
                    <li className="flex items-start py-2">
                      <Check className="h-5 w-5 text-green-500 mr-2 shrink-0 mt-0.5" />
                      <span className="font-bold text-white">3 x 2 Exhibit Space (6 sqm)</span>
                    </li>
                    <Separator className="bg-white/25 my-1" />
                    <li className="flex items-start py-2">
                      <Check className="h-5 w-5 text-green-500 mr-2 shrink-0 mt-0.5" />
                      <span className="text-gray-300">Electricity, Wifi, Table and 2 Chairs</span>
                    </li>
                    <Separator className="bg-white/25 my-1" />
                    <li className="flex items-start py-2">
                      <Check className="h-5 w-5 text-green-500 mr-2 shrink-0 mt-0.5" />
                      <span className="text-gray-300">Daily Lunches and Teas</span>
                    </li>
                    <Separator className="bg-white/25 my-1" />
                    <li className="flex items-start py-2">
                      <Check className="h-5 w-5 text-green-500 mr-2 shrink-0 mt-0.5" />
                      <span className="text-gray-300">Access to Delegates List & Slides</span>
                    </li>
                    <Separator className="bg-white/25 my-1" />
                    <li className="flex items-start py-2">
                      <Check className="h-5 w-5 text-green-500 mr-2 shrink-0 mt-0.5" />
                      <span className="text-gray-300">Access to Networking App</span>
                    </li>
                  </ul>
                </div>
                <CardFooter className="p-6 pt-0 flex flex-col gap-3">
                  <Button 
                    onClick={() => window.open("https://aiworldcongress.myshopify.com/products/exhibit-packages-2026-nov", "_blank")} 
                    className="w-full bg-gradient-to-r from-blue-600 to-cyan-400 hover:from-blue-700 hover:to-cyan-500 text-white text-xs"
                    size="lg"
                  >
                    BOOK EXHIBIT SPACE
                  </Button>
                  <Button 
                    onClick={() => window.open("https://booking.aiconference.london/products/ai-world-congress-2026-nov", "_blank")}
                    className="w-full bg-gradient-to-r from-[#F97316] to-[#ea384c] hover:opacity-90 text-white text-xs"
                    size="lg"
                  >
                    BOOK DELEGATE PASSES
                  </Button>
                </CardFooter>
              </Card>
              
              {/* Package 2 */}
              <Card className="bg-gradient-to-br from-purple-900 via-purple-900/80 to-black border border-purple-500/30 overflow-hidden relative rounded-xl shadow-lg shadow-purple-900/20 hover:shadow-purple-800/30 hover:-translate-y-1 transition-all duration-300">
                <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-blue-500 to-purple-600"></div>
                <div className="p-6">
                  <div className="mb-6 text-center">
                    <h3 className="text-xl font-bold text-gray-300 uppercase tracking-wider mb-1">Package 2</h3>
                    <div className="flex justify-center items-baseline">
                      <span className="text-5xl font-bold text-white">£3,850</span>
                      <span className="text-gray-400 ml-1">+VAT</span>
                    </div>
                  </div>
                  <Separator className="bg-purple-600/40 my-6" />
                  <ul className="space-y-0 mb-8">
                    <li className="flex items-start py-2">
                      <Check className="h-5 w-5 text-green-500 mr-2 shrink-0 mt-0.5" />
                      <span className="text-gray-300">3 x Delegate Passes</span>
                    </li>
                    <Separator className="bg-white/25 my-1" />
                    <li className="flex items-start py-2">
                      <Check className="h-5 w-5 text-green-500 mr-2 shrink-0 mt-0.5" />
                      <span className="font-bold text-white">3 x 2 Exhibit Space (6 sqm)</span>
                    </li>
                    <Separator className="bg-white/25 my-1" />
                    <li className="flex items-start py-2">
                      <Check className="h-5 w-5 text-green-500 mr-2 shrink-0 mt-0.5" />
                      <span className="text-gray-300">Electricity, Wifi, Table and 2 Chairs</span>
                    </li>
                    <Separator className="bg-white/25 my-1" />
                    <li className="flex items-start py-2">
                      <Check className="h-5 w-5 text-green-500 mr-2 shrink-0 mt-0.5" />
                      <span className="text-gray-300">Daily Lunches and Teas</span>
                    </li>
                    <Separator className="bg-white/25 my-1" />
                    <li className="flex items-start py-2">
                      <Check className="h-5 w-5 text-green-500 mr-2 shrink-0 mt-0.5" />
                      <span className="text-gray-300">Access to Delegates List & Slides</span>
                    </li>
                    <Separator className="bg-white/25 my-1" />
                    <li className="flex items-start py-2">
                      <Check className="h-5 w-5 text-green-500 mr-2 shrink-0 mt-0.5" />
                      <span className="text-gray-300">Access to Networking App</span>
                    </li>
                  </ul>
                </div>
                <CardFooter className="p-6 pt-0 flex flex-col gap-3">
                  <Button 
                    onClick={() => window.open("https://aiworldcongress.myshopify.com/products/exhibit-packages-2026-nov?variant=52258495037783", "_blank")} 
                    className="w-full bg-gradient-to-r from-blue-600 to-cyan-400 hover:from-blue-700 hover:to-cyan-500 text-white text-xs"
                    size="lg"
                  >
                    BOOK EXHIBIT SPACE
                  </Button>
                  <Button 
                    onClick={() => window.open("https://booking.aiconference.london/products/ai-world-congress-2026-nov?variant=53889774780759", "_blank")}
                    className="w-full bg-gradient-to-r from-[#F97316] to-[#ea384c] hover:opacity-90 text-white text-xs"
                    size="lg"
                  >
                    BOOK DELEGATE PASSES
                  </Button>
                </CardFooter>
              </Card>
              
              {/* Package 3 */}
              <Card className="bg-gradient-to-br from-purple-900 via-purple-900/80 to-black border border-purple-500/30 overflow-hidden relative rounded-xl shadow-lg shadow-purple-900/20 hover:shadow-purple-800/30 hover:-translate-y-1 transition-all duration-300">
                <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-blue-500 to-purple-600"></div>
                <div className="p-6">
                  <div className="mb-6 text-center">
                    <h3 className="text-xl font-bold text-gray-300 uppercase tracking-wider mb-1">Package 3</h3>
                    <div className="flex justify-center items-baseline">
                      <span className="text-5xl font-bold text-white">£4,850</span>
                      <span className="text-gray-400 ml-1">+VAT</span>
                    </div>
                  </div>
                  <Separator className="bg-purple-600/40 my-6" />
                  <ul className="space-y-0 mb-8">
                    <li className="flex items-start py-2">
                      <Check className="h-5 w-5 text-green-500 mr-2 shrink-0 mt-0.5" />
                      <span className="text-gray-300">5 x Delegate Passes</span>
                    </li>
                    <Separator className="bg-white/25 my-1" />
                    <li className="flex items-start py-2">
                      <Check className="h-5 w-5 text-green-500 mr-2 shrink-0 mt-0.5" />
                      <span className="font-bold text-white">3 x 3 Exhibit Space (9 sqm)</span>
                    </li>
                    <Separator className="bg-white/25 my-1" />
                    <li className="flex items-start py-2">
                      <Check className="h-5 w-5 text-green-500 mr-2 shrink-0 mt-0.5" />
                      <span className="text-gray-300">Electricity, Wifi, Table and 2 Chairs</span>
                    </li>
                    <Separator className="bg-white/25 my-1" />
                    <li className="flex items-start py-2">
                      <Check className="h-5 w-5 text-green-500 mr-2 shrink-0 mt-0.5" />
                      <span className="text-gray-300">Daily Lunches and Teas</span>
                    </li>
                    <Separator className="bg-white/25 my-1" />
                    <li className="flex items-start py-2">
                      <Check className="h-5 w-5 text-green-500 mr-2 shrink-0 mt-0.5" />
                      <span className="text-gray-300">Access to Delegates List & Slides</span>
                    </li>
                    <Separator className="bg-white/25 my-1" />
                    <li className="flex items-start py-2">
                      <Check className="h-5 w-5 text-green-500 mr-2 shrink-0 mt-0.5" />
                      <span className="text-gray-300">Access to Networking App</span>
                    </li>
                  </ul>
                </div>
                <CardFooter className="p-6 pt-0 flex flex-col gap-3">
                  <Button 
                    onClick={() => window.open("https://aiworldcongress.myshopify.com/products/exhibit-packages-2026-nov?variant=52258495070551", "_blank")} 
                    className="w-full bg-gradient-to-r from-blue-600 to-cyan-400 hover:from-blue-700 hover:to-cyan-500 text-white text-xs"
                    size="lg"
                  >
                    BOOK EXHIBIT SPACE
                  </Button>
                  <Button 
                    onClick={() => window.open("https://booking.aiconference.london/products/ai-world-congress-2026-nov?variant=53889774813527", "_blank")}
                    className="w-full bg-gradient-to-r from-[#F97316] to-[#ea384c] hover:opacity-90 text-white text-xs"
                    size="lg"
                  >
                    BOOK DELEGATE PASSES
                  </Button>
                </CardFooter>
              </Card>
            </div>
            <p className="text-center mt-6 text-gray-400">All prices subject to 20% VAT Tax.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-16">
            <div className="bg-black/50 p-8 rounded-lg border border-purple-500/20">
              <h2 className="text-2xl font-bold text-white mb-4">Why Exhibit?</h2>
              <ul className="space-y-3">
                {[
                  "Connect with AI professionals",
                  "Showcase your latest AI solutions",
                  "Generate high-quality leads",
                  "Network with industry leaders",
                  "Meet potential investors and partners"
                ].map((item, index) => (
                  <li key={index} className="flex items-start">
                    <Check className="h-5 w-5 text-green-500 mr-2 shrink-0 mt-0.5" />
                    <span className="text-gray-300">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="bg-black/50 p-8 rounded-lg border border-purple-500/20">
              <h2 className="text-2xl font-bold text-white mb-4">Stand Options</h2>
              <div className="space-y-6">
                <div className="border-b border-gray-700 pb-4">
                  <div className="flex justify-between items-center mb-2">
                    <h3 className="text-xl text-white">Exhibit Package 1</h3>
                    <span className="text-purple-400 font-bold">£2,850</span>
                  </div>
                  <p className="text-sm text-gray-400">2 conference passes with 6 sqm exhibition space</p>
                </div>
                
                <div className="border-b border-gray-700 pb-4">
                  <div className="flex justify-between items-center mb-2">
                    <h3 className="text-xl text-white">Exhibit Package 2</h3>
                    <span className="text-purple-400 font-bold">£3,850</span>
                  </div>
                  <p className="text-sm text-gray-400">3 conference passes with 6 sqm exhibition space</p>
                </div>
                
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <h3 className="text-xl text-white">Exhibit Package 3</h3>
                    <span className="text-purple-400 font-bold">£4,850</span>
                  </div>
                  <p className="text-sm text-gray-400">5 conference passes with 9 sqm exhibition space</p>
                </div>
              </div>
              
              <div className="mt-8 space-y-4">
                <Button 
                  onClick={() => window.open("https://aiworldcongress.myshopify.com/products/exhibit-packages-2026-nov", "_blank")} 
                  className="w-full bg-gradient-to-r from-blue-600 to-cyan-400 hover:from-blue-700 hover:to-cyan-500 text-white"
                  size="lg"
                >
                  BOOK EXHIBIT SPACE
                </Button>
                <Button 
                  onClick={() => window.open("https://aiconference.london/register", "_blank")}
                  className="w-full bg-gradient-to-r from-[#F97316] to-[#ea384c] hover:opacity-90 text-white"
                  size="lg"
                >
                  BOOK DELEGATE PASS!
                </Button>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-purple-900/70 to-blue-900/70 p-8 rounded-lg text-center mb-12">
            <h2 className="text-2xl font-bold text-white mb-4">Ready to Book Your Stand?</h2>
            <p className="text-gray-300 mb-6">
              Contact our exhibitions team to discuss available options and secure your space at AI Summit.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button size="lg" className="bg-purple-600 hover:bg-purple-700 text-white">
                  <Book className="mr-2 h-5 w-5" />
                  Download Exhibitor Pack
                </Button>
              </Link>
              <Link to="/contact">
                <Button size="lg" className="bg-white text-purple-900 hover:bg-gray-100">
                  Contact Sales Team
                  <ChevronRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {[
              {
                title: "Prime Location",
                description: "All stands are positioned in high-traffic areas to maximize visitor engagement."
              },
              {
                title: "Marketing Support",
                description: "Your company featured in our event website, app, and promotional materials."
              },
              {
                title: "Networking Events",
                description: "Exclusive access to exhibitor-only networking sessions and VIP events."
              }
            ].map((feature, index) => (
              <div key={index} className="bg-black/40 p-6 rounded-lg">
                <h3 className="text-xl font-bold text-white mb-2">{feature.title}</h3>
                <p className="text-gray-400">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
      <LogoScroller />
      <Footer />
    </div>
  );
};

export default BookStand;
